import React, { useState, useEffect } from 'react';
import { getAuth, onIdTokenChanged } from "firebase/auth";
import { useUserContext } from "./userProvider";
import { getUser } from "src/apiCalls";
import PersonalDetailsForm from "src/content/pages/User/PersonalDetails";
import { CircularProgress, Box, Button, Typography } from '@mui/material';
import { SignInModal } from '../Auth/SignInModal';
import logger from 'src/logger';

export const ProtectedRoute = ({ children }) => {
  const { user, refreshToken } = useUserContext();
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setLoading] = useState(true);
  const [hasUserProfile, setHasUserProfile] = useState(null);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const auth = getAuth();

  useEffect(() => {
    const unsubscribeTokenChange = onIdTokenChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          await firebaseUser.getIdToken(true);
        } catch (error) {
          logger.error('Error refreshing token:', error);
        }
      }
      setLoading(false);
    });

    return () => unsubscribeTokenChange();
  }, [auth]);

  useEffect(() => {
    if (!user) {
      setHasUserProfile(false);
      setLoading(false);
      return;
    }

    const checkUserProfile = async () => {
      try {
        let profile = await getUser(user.accessToken, user.uid);

        // Only retry if we get an error, not if user doesn't exist (null response)
        if (!profile) {
          // User doesn't exist - this is legitimate, not an error
          setHasUserProfile(false);
        } else {
          // User exists
          setHasUserProfile(Boolean(profile?.id));
        }

      } catch (error) {
        // Only retry on actual errors, not when user doesn't exist
        logger.error('Error fetching user profile:', error);
        
        // Try to refresh token and retry once more
        try {
          const newToken = await refreshToken();
          if (newToken) {
            const retryProfile = await getUser(newToken, user.uid);
            if (retryProfile) {
              setHasUserProfile(Boolean(retryProfile?.id));
            } else {
              setHasUserProfile(false);
            }
          } else {
            setErrorMessage('User could not be loaded. Please try again later.');
          }
        } catch (retryError) {
          logger.error('Error during retry:', retryError);
          setErrorMessage('User could not be loaded. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    checkUserProfile();
  }, [user, refreshToken]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          padding: 2,
        }}
      >
        <Box
          sx={{
            backgroundColor: 'background.paper',
            padding: 4,
            borderRadius: 2,
            boxShadow: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" gutterBottom>
            Welcome to Photo App
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Sign in to access your account and start sharing photos
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => setShowSignInModal(true)}
            sx={{ px: 4, py: 1.5 }}
          >
            Sign In
          </Button>
        </Box>
        
        <SignInModal 
          open={showSignInModal} 
          onClose={() => setShowSignInModal(false)} 
        />
      </Box>
    );
  }

  if (errorMessage) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          textAlign: 'center',
        }}
      >
        <Typography variant="h6" color="error">
          {errorMessage}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  if (hasUserProfile === false) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <PersonalDetailsForm
          initialName={user.displayName}
          initialEmail={user.email}
          id={user.uid}
          isNewUser={!hasUserProfile}
        />
      </Box>
    );
  }

  return children;
};
